<script setup>
import { reactive, ref, watch, onUnmounted } from "vue"
import Swal from "sweetalert2"
import api from "@/api/axios.js"

const props = defineProps({
  visible: Boolean,
  cita: { type: Object, default: null }
})

const emit = defineEmits(["close", "guardado"])

const DIAS_SEMANA = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"]

const asesoresLideres = ref([])
const ciudades = ref([])
const slotsLibres = ref([])
const cargandoSlots = ref(false)
const guardando = ref(false)
const catalogosCargados = ref(false)

const form = reactive({
  fecha: "",
  slot: "",
  nombreCliente: "",
  celular: "",
  interesadoEn: "",
  asesorLiderId: "",
  ciudadAtencionId: "",
  tipo: "presencial"
})

const linkReunion = ref("")

const diaSemanaTexto = ref("")

watch(() => form.fecha, (fecha) => {
  if (!fecha) {
    diaSemanaTexto.value = ""
    return
  }
  const [year, month, day] = fecha.split("-").map(Number)
  diaSemanaTexto.value = DIAS_SEMANA[new Date(year, month - 1, day).getDay()]
})

async function cargarCatalogosBase() {
  if (catalogosCargados.value) return
  try {
    const [rAsesores, rCiudades] = await Promise.all([
      api.get("/citas/asesores-lideres"),
      api.get("/catalogos/ciudadAtencion")
    ])
    asesoresLideres.value = rAsesores.data.asesoresLideres
    ciudades.value = rCiudades.data.items
    catalogosCargados.value = true
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudieron cargar los datos base",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

// Idempotente a propósito: se dispara tanto por el watcher de asesor/fecha
// como por la carga inicial del formulario, y ambas rutas pueden solaparse.
// Conserva form.slot si sigue apareciendo entre los libres (la disponibilidad
// ya excluye la propia cita vía citaId), y lo limpia solo si dejó de ser válido.
function alCambiarAsesor() {
  const asesor = asesoresLideres.value.find((a) => a.id === form.asesorLiderId)
  if (asesor?.ciudadAtencionId) form.ciudadAtencionId = asesor.ciudadAtencionId
}

async function cargarDisponibilidad() {
  if (!form.asesorLiderId || !form.fecha) {
    slotsLibres.value = []
    form.slot = ""
    return
  }

  cargandoSlots.value = true
  try {
    const { data } = await api.get("/citas/disponibilidad", {
      params: { asesorLiderId: form.asesorLiderId, fecha: form.fecha, citaId: props.cita?.id }
    })
    slotsLibres.value = data.slotsLibres
    const slotSigueValido = slotsLibres.value.some((s) => `${s.horaInicio}|${s.horaFin}` === form.slot)
    if (!slotSigueValido) form.slot = ""
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar la disponibilidad",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    cargandoSlots.value = false
  }
}

watch(() => [form.asesorLiderId, form.fecha], cargarDisponibilidad)

watch(
  () => props.visible,
  async (visible) => {
    if (!visible || !props.cita) return

    await cargarCatalogosBase()

    const c = props.cita
    form.nombreCliente = c.nombreCliente
    form.celular = c.celular
    form.interesadoEn = c.interesadoEn || ""
    form.ciudadAtencionId = c.ciudadAtencionId || ""
    form.slot = `${c.horaInicio}|${c.horaFin}`
    form.asesorLiderId = c.asesorLiderId
    form.fecha = String(c.fecha).slice(0, 10)
    form.tipo = c.tipo || "presencial"
    linkReunion.value = c.linkReunion || ""
  },
  { immediate: true }
)

watch(
  () => props.visible,
  (visible) => {
    document.body.style.overflow = visible ? "hidden" : ""
  }
)

onUnmounted(() => {
  document.body.style.overflow = ""
})

async function guardar() {
  if (!form.fecha || !form.slot || !form.nombreCliente || !form.celular || !form.asesorLiderId || !form.ciudadAtencionId) {
    Swal.fire({ icon: "warning", title: "Faltan datos", text: "Completa fecha, horario, cliente, celular, asesor líder y ciudad de atención." })
    return
  }

  const [horaInicio, horaFin] = form.slot.split("|")

  guardando.value = true
  try {
    const { data } = await api.put(`/citas/${props.cita.id}`, {
      fecha: form.fecha,
      horaInicio,
      horaFin,
      nombreCliente: form.nombreCliente,
      celular: form.celular,
      interesadoEn: form.interesadoEn || null,
      asesorLiderId: form.asesorLiderId,
      ciudadAtencionId: form.ciudadAtencionId || null,
      tipo: form.tipo
    })

    if (data.tipo === "virtual" && data.linkReunion && data.linkReunion !== linkReunion.value) {
      linkReunion.value = data.linkReunion
      Swal.fire({
        icon: "success",
        title: "Cita actualizada",
        html: `Link de Google Meet para la reunión:<br><a href="${data.linkReunion}" target="_blank" rel="noopener">${data.linkReunion}</a>`
      })
    } else {
      Swal.fire({ icon: "success", title: "Cita actualizada", timer: 1500, showConfirmButton: false })
    }
    emit("guardado")
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo actualizar la cita",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    guardando.value = false
  }
}
</script>

<template>
  <template v-if="visible">
    <div class="modal-backdrop-custom"></div>
    <div class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="ni ni-calendar-grid-58 text-primary me-2"></i>Editar Cita</h5>
            <button class="btn-close" @click="$emit('close')"></button>
          </div>

          <div class="modal-body">
            <form class="row g-3" @submit.prevent="guardar">
              <div class="col-12">
                <label class="form-label">Tipo de cita *</label>
                <div class="d-flex gap-3">
                  <div class="form-check">
                    <input class="form-check-input" type="radio" name="tipoCitaEdit" id="tipoPresencialEdit" value="presencial" v-model="form.tipo">
                    <label class="form-check-label" for="tipoPresencialEdit">Presencial</label>
                  </div>
                  <div class="form-check">
                    <input class="form-check-input" type="radio" name="tipoCitaEdit" id="tipoVirtualEdit" value="virtual" v-model="form.tipo">
                    <label class="form-check-label" for="tipoVirtualEdit">Virtual</label>
                  </div>
                </div>
                <small v-if="form.tipo === 'virtual' && !linkReunion" class="text-muted">
                  Al guardar se generará automáticamente un link de Google Meet para la reunión.
                </small>
                <div v-if="form.tipo === 'virtual' && linkReunion" class="text-sm mt-1">
                  <i class="bi bi-camera-video text-primary me-1"></i>
                  <a :href="linkReunion" target="_blank" rel="noopener">{{ linkReunion }}</a>
                </div>
              </div>
              <div class="col-md-4">
                <label class="form-label">Asesor líder *</label>
                <select class="form-select" v-model="form.asesorLiderId" required @change="alCambiarAsesor">
                  <option value="">Selecciona...</option>
                  <option v-for="a in asesoresLideres" :key="a.id" :value="a.id">{{ a.name }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">Fecha *</label>
                <input type="date" class="form-control" v-model="form.fecha" required>
                <small v-if="diaSemanaTexto" class="text-muted">{{ diaSemanaTexto }}</small>
              </div>
              <div class="col-md-4">
                <label class="form-label">Ciudad de atención *</label>
                <select class="form-select" v-model="form.ciudadAtencionId" required>
                  <option value="" disabled>Selecciona...</option>
                  <option v-for="c in ciudades" :key="c.id" :value="c.id">{{ c.nombre }}</option>
                </select>
              </div>

              <div class="col-12">
                <label class="form-label">Horario disponible *</label>
                <div v-if="cargandoSlots" class="text-muted text-sm">Cargando horarios...</div>
                <div v-else-if="!form.asesorLiderId || !form.fecha" class="text-muted text-sm">
                  Selecciona un asesor líder y una fecha para ver los horarios disponibles.
                </div>
                <div v-else-if="slotsLibres.length === 0" class="text-muted text-sm">
                  No hay horarios disponibles para esa fecha.
                </div>
                <div v-else class="d-flex flex-wrap gap-2">
                  <button
                    v-for="s in slotsLibres"
                    :key="s.horaInicio"
                    type="button"
                    class="btn btn-sm"
                    :class="form.slot === `${s.horaInicio}|${s.horaFin}` ? 'btn-primary' : 'btn-outline-primary'"
                    @click="form.slot = `${s.horaInicio}|${s.horaFin}`"
                  >
                    {{ s.horaInicio }}
                  </button>
                </div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Nombre del cliente *</label>
                <input type="text" class="form-control" v-model="form.nombreCliente" required>
              </div>
              <div class="col-md-6">
                <label class="form-label">Celular *</label>
                <input type="text" class="form-control" v-model="form.celular" required>
              </div>
              <div class="col-12">
                <label class="form-label">¿En qué está interesado?</label>
                <input type="text" class="form-control" v-model="form.interesadoEn" placeholder="Ej: Plan todo incluido Cancún">
              </div>

              <div class="col-12 d-flex justify-content-end gap-2">
                <button type="button" class="btn btn-outline-secondary mb-0" @click="$emit('close')">Cancelar</button>
                <button type="submit" class="btn btn-primary mb-0" :disabled="guardando || !form.slot || !form.ciudadAtencionId">
                  {{ guardando ? "Guardando..." : "Guardar cambios" }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 1040;
}

.modal {
  z-index: 1050;
}
</style>
